import { useState, useEffect, useCallback } from 'react';
import {
  Plus, X, Send, Clock, CheckCircle2, AlertTriangle, User,
  ChevronDown, ChevronRight, Trash2, MessageSquare, Flag,
  ArrowLeft, Users, Edit2, Filter
} from 'lucide-react';
import getApiUrl from '../utils/getApiUrl';

const API = `${getApiUrl()}/api`;

const authFetch = (url, options = {}) => {
  const token = localStorage.getItem("auth_token");
  return fetch(url, {
    ...options,
    credentials: "include",
    headers: { ...(options.headers || {}), ...(token ? { Authorization: `Bearer ${token}` } : {}) },
  });
};


const PRIORITY_CONFIG = {
  urgent: { bg: 'bg-red-500/20', border: 'border-red-500/30', text: 'text-red-400', label: 'Urgent', dot: 'bg-red-500' },
  normal: { bg: 'bg-blue-500/20', border: 'border-blue-500/30', text: 'text-blue-400', label: 'Normal', dot: 'bg-blue-500' },
  basse: { bg: 'bg-slate-500/20', border: 'border-slate-500/30', text: 'text-slate-400', label: 'Basse', dot: 'bg-slate-500' }
};

const STATUS_CONFIG = {
  a_faire: { label: 'A faire', bg: 'bg-orange-500/20', text: 'text-orange-400', icon: Clock },
  en_cours: { label: 'En cours', bg: 'bg-blue-500/20', text: 'text-blue-400', icon: ChevronRight },
  terminee: { label: 'Terminee', bg: 'bg-emerald-500/20', text: 'text-emerald-400', icon: CheckCircle2 }
};

// ─── Client Search Autocomplete ───────────────────────────────
const ClientAutocomplete = ({ value, onChange, onSelect }) => {
  const [suggestions, setSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [searching, setSearching] = useState(false);

  const searchClients = async (query) => {
    if (!query || query.length < 2) { setSuggestions([]); return; }
    setSearching(true);
    try {
      const r = await authFetch(`${API}/search/autocomplete?q=${encodeURIComponent(query)}&field=nom&limit=8`);
      if (r.ok) {
        const d = await r.json();
        setSuggestions(d.suggestions || []);
        setShowSuggestions(true);
      }
    } catch (e) { console.error(e); }
    setSearching(false);
  };

  useEffect(() => {
    const timer = setTimeout(() => searchClients(value), 300);
    return () => clearTimeout(timer);
  }, [value]);

  return (
    <div className="relative">
      <input
        value={value}
        onChange={e => { onChange(e.target.value); }}
        onFocus={() => suggestions.length > 0 && setShowSuggestions(true)}
        onBlur={() => setTimeout(() => setShowSuggestions(false), 200)}
        className="w-full p-2.5 bg-slate-800 border border-slate-700 rounded-lg text-white placeholder-slate-500 focus:ring-2 focus:ring-blue-500 focus:outline-none"
        placeholder="Rechercher un client..."
        data-testid="task-client-search"
      />
      {searching && <div className="absolute right-3 top-3"><div className="w-4 h-4 border-2 border-blue-400 border-t-transparent rounded-full animate-spin" /></div>}
      {showSuggestions && suggestions.length > 0 && (
        <div className="absolute z-20 w-full mt-1 bg-slate-800 border border-slate-700 rounded-lg max-h-48 overflow-y-auto shadow-xl">
          {suggestions.map((name, idx) => (
            <button
              key={idx}
              onMouseDown={() => { onSelect(name); setShowSuggestions(false); }}
              className="w-full text-left px-3 py-2 text-sm text-slate-300 hover:bg-slate-700 hover:text-white flex items-center gap-2"
              data-testid={`client-suggestion-${idx}`}
            >
              <User className="w-3.5 h-3.5 text-slate-500" />
              {name}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

// ─── Task Create Modal ────────────────────────────────────────
const TaskCreateModal = ({ onClose, onCreate, users, clientId, clientName, authorEmail, authorName }) => {
  const [form, setForm] = useState({
    title: '', description: '', priority: 'normal',
    assigned_to: [], client_id: clientId || '', client_name: clientName || ''
  });
  const [saving, setSaving] = useState(false);
  const [showUserPicker, setShowUserPicker] = useState(false);

  const toggleAssignee = (email) => {
    setForm(prev => ({
      ...prev,
      assigned_to: prev.assigned_to.includes(email)
        ? prev.assigned_to.filter(e => e !== email)
        : [...prev.assigned_to, email]
    }));
  };

  const handleSubmit = async () => {
    if (!form.title.trim()) return;
    setSaving(true);
    try {
      const r = await authFetch(`${API}/tasks?user_email=${encodeURIComponent(authorEmail)}&user_name=${encodeURIComponent(authorName || '')}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(form)
      });
      if (r.ok) {
        const task = await r.json();
        onCreate(task);
        onClose();
      }
    } catch (e) { console.error(e); }
    setSaving(false);
  };

  return (
    <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4" data-testid="task-create-modal">
      <div className="bg-slate-900 border border-slate-700 rounded-xl max-w-lg w-full p-6">
        <div className="flex items-center justify-between mb-5">
          <h3 className="text-lg font-bold text-white flex items-center gap-2">
            <Plus className="w-5 h-5 text-blue-400" /> Nouvelle tache
          </h3>
          <button onClick={onClose} className="text-slate-400 hover:text-white"><X className="w-5 h-5" /></button>
        </div>

        <div className="space-y-4">
          <div>
            <label className="block text-sm text-slate-400 mb-1">Titre *</label>
            <input
              data-testid="task-title-input"
              value={form.title}
              onChange={e => setForm({ ...form, title: e.target.value })}
              className="w-full p-2.5 bg-slate-800 border border-slate-700 rounded-lg text-white placeholder-slate-500 focus:ring-2 focus:ring-blue-500 focus:outline-none"
              placeholder="Ex: Relancer le client pour le devis..."
            />
          </div>

          <div>
            <label className="block text-sm text-slate-400 mb-1">Description</label>
            <textarea
              data-testid="task-desc-input"
              value={form.description}
              onChange={e => setForm({ ...form, description: e.target.value })}
              className="w-full p-2.5 bg-slate-800 border border-slate-700 rounded-lg text-white resize-none placeholder-slate-500 focus:ring-2 focus:ring-blue-500 focus:outline-none"
              rows={3}
              placeholder="Details..."
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm text-slate-400 mb-1">Priorite</label>
              <select
                data-testid="task-priority-select"
                value={form.priority}
                onChange={e => setForm({ ...form, priority: e.target.value })}
                className="w-full p-2.5 bg-slate-800 border border-slate-700 rounded-lg text-white"
              >
                <option value="basse">Basse</option>
                <option value="normal">Normal</option>
                <option value="urgent">Urgent</option>
              </select>
            </div>

            {!clientId && (
              <div>
                <label className="block text-sm text-slate-400 mb-1">Client</label>
                <ClientAutocomplete
                  value={form.client_name}
                  onChange={v => setForm({ ...form, client_name: v })}
                  onSelect={name => setForm({ ...form, client_name: name })}
                />
              </div>
            )}
          </div>

          {/* Assignees */}
          <div>
            <label className="block text-sm text-slate-400 mb-1">Assigner a</label>
            <div
              className="p-2.5 bg-slate-800 border border-slate-700 rounded-lg cursor-pointer min-h-[42px] flex flex-wrap gap-1.5 items-center"
              onClick={() => setShowUserPicker(!showUserPicker)}
              data-testid="task-assignee-picker"
            >
              {form.assigned_to.length === 0 && <span className="text-slate-500 text-sm">Selectionner...</span>}
              {form.assigned_to.map(email => {
                const u = users.find(u => u.email === email);
                return (
                  <span key={email} className="px-2 py-0.5 bg-blue-500/20 text-blue-400 rounded text-xs flex items-center gap-1">
                    {u?.name || email.split('@')[0]}
                    <X className="w-3 h-3 cursor-pointer hover:text-white" onClick={e => { e.stopPropagation(); toggleAssignee(email); }} />
                  </span>
                );
              })}
            </div>
            {showUserPicker && (
              <div className="mt-1 bg-slate-800 border border-slate-700 rounded-lg max-h-40 overflow-y-auto">
                {users.map(u => (
                  <button
                    key={u.email}
                    onClick={() => toggleAssignee(u.email)}
                    className={`w-full text-left px-3 py-2 text-sm hover:bg-slate-700 flex items-center justify-between ${
                      form.assigned_to.includes(u.email) ? 'bg-blue-500/10 text-blue-400' : 'text-slate-300'
                    }`}
                  >
                    <span>{u.name}</span>
                    {form.assigned_to.includes(u.email) && <CheckCircle2 className="w-4 h-4" />}
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>

        <div className="flex gap-3 mt-6">
          <button onClick={onClose} className="flex-1 px-4 py-2.5 bg-slate-700 hover:bg-slate-600 rounded-lg text-white">Annuler</button>
          <button
            data-testid="task-create-submit"
            onClick={handleSubmit}
            disabled={saving || !form.title.trim()}
            className="flex-1 px-4 py-2.5 bg-blue-600 hover:bg-blue-500 disabled:opacity-50 rounded-lg text-white font-medium"
          >
            {saving ? 'Creation...' : 'Creer'}
          </button>
        </div>
      </div>
    </div>
  );
};

// ─── Task Detail View ─────────────────────────────────────────
const TaskDetailView = ({ task, onClose, onUpdate, users, authorEmail, authorName }) => {
  const [replyText, setReplyText] = useState('');
  const [sending, setSending] = useState(false);
  const [currentTask, setCurrentTask] = useState(task);
  const [editingStatus, setEditingStatus] = useState(false);

  const prio = PRIORITY_CONFIG[currentTask.priority] || PRIORITY_CONFIG.normal;
  const stat = STATUS_CONFIG[currentTask.status] || STATUS_CONFIG.a_faire;
  const StatusIcon = stat.icon;

  const sendReply = async () => {
    if (!replyText.trim()) return;
    setSending(true);
    try {
      const r = await authFetch(`${API}/tasks/${currentTask.task_id}/reply`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ text: replyText, author_email: authorEmail, author_name: authorName })
      });
      if (r.ok) {
        const updated = await r.json();
        setCurrentTask(updated);
        setReplyText('');
        onUpdate(updated);
      }
    } catch (e) { console.error(e); }
    setSending(false);
  };

  const changeStatus = async (newStatus) => {
    try {
      const r = await authFetch(`${API}/tasks/${currentTask.task_id}?user_email=${encodeURIComponent(authorEmail)}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ status: newStatus })
      });
      if (r.ok) {
        const updated = await r.json();
        setCurrentTask(updated);
        onUpdate(updated);
      }
    } catch (e) { console.error(e); }
    setEditingStatus(false);
  };

  const timeAgo = (dateStr) => {
    if (!dateStr) return '';
    const diff = (Date.now() - new Date(dateStr).getTime()) / 1000;
    if (diff < 60) return 'maintenant';
    if (diff < 3600) return `il y a ${Math.floor(diff / 60)}min`;
    if (diff < 86400) return `il y a ${Math.floor(diff / 3600)}h`;
    return new Date(dateStr).toLocaleDateString('fr-FR', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' });
  };

  return (
    <div className="flex flex-col h-full" data-testid="task-detail-view">
      {/* Header */}
      <div className="p-4 border-b border-slate-700 flex items-center gap-3">
        <button onClick={onClose} className="p-1.5 hover:bg-slate-800 rounded-lg" data-testid="task-detail-back">
          <ArrowLeft className="w-5 h-5 text-slate-400" />
        </button>
        <div className="flex-1 min-w-0">
          <h3 className="text-white font-bold truncate">{currentTask.title}</h3>
          <div className="flex items-center gap-2 mt-1">
            <span className={`text-xs px-2 py-0.5 rounded ${prio.bg} ${prio.text}`}>{prio.label}</span>
            <div className="relative">
              <button
                onClick={() => setEditingStatus(!editingStatus)}
                className={`text-xs px-2 py-0.5 rounded flex items-center gap-1 ${stat.bg} ${stat.text} hover:opacity-80`}
                data-testid="task-status-btn"
              >
                <StatusIcon className="w-3 h-3" /> {stat.label}
                <ChevronDown className="w-3 h-3" />
              </button>
              {editingStatus && (
                <div className="absolute top-full left-0 mt-1 bg-slate-800 border border-slate-700 rounded-lg z-10 overflow-hidden shadow-xl">
                  {Object.entries(STATUS_CONFIG).map(([key, cfg]) => (
                    <button
                      key={key}
                      onClick={() => changeStatus(key)}
                      className={`w-full text-left px-3 py-2 text-sm hover:bg-slate-700 flex items-center gap-2 ${
                        currentTask.status === key ? `${cfg.text} font-medium` : 'text-slate-300'
                      }`}
                      data-testid={`task-status-${key}`}
                    >
                      <cfg.icon className="w-4 h-4" /> {cfg.label}
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Body */}
      <div className="flex-1 overflow-y-auto p-4 space-y-4">
        {/* Description */}
        {currentTask.description && (
          <div className="bg-slate-800/50 rounded-lg p-3 border border-slate-700/50">
            <p className="text-sm text-slate-300">{currentTask.description}</p>
          </div>
        )}

        {/* Meta */}
        <div className="grid grid-cols-2 gap-3 text-sm">
          <div className="bg-slate-800/50 rounded-lg p-3 border border-slate-700/50">
            <p className="text-slate-500 text-xs mb-1">Creee par</p>
            <p className="text-white">{currentTask.created_by_name || currentTask.created_by || 'Inconnu'}</p>
            <p className="text-slate-500 text-xs">{timeAgo(currentTask.created_at)}</p>
          </div>
          <div className="bg-slate-800/50 rounded-lg p-3 border border-slate-700/50">
            <p className="text-slate-500 text-xs mb-1">Assignee a</p>
            <div className="flex flex-wrap gap-1">
              {(currentTask.assigned_to || []).map(email => (
                <span key={email} className="px-2 py-0.5 bg-blue-500/20 text-blue-400 rounded text-xs">
                  {users.find(u => u.email === email)?.name || email.split('@')[0]}
                </span>
              ))}
              {(!currentTask.assigned_to || currentTask.assigned_to.length === 0) && (
                <span className="text-slate-500">Non assigne</span>
              )}
            </div>
          </div>
        </div>

        {currentTask.client_name && (
          <div className="bg-slate-800/50 rounded-lg p-3 border border-slate-700/50 text-sm">
            <p className="text-slate-500 text-xs mb-1">Client</p>
            <p className="text-white flex items-center gap-2"><User className="w-4 h-4 text-slate-400" />{currentTask.client_name}</p>
          </div>
        )}

        {/* Thread */}
        <div>
          <h4 className="text-sm font-medium text-slate-400 mb-3 flex items-center gap-2">
            <MessageSquare className="w-4 h-4" /> Fil de discussion ({(currentTask.thread || []).length})
          </h4>
          <div className="space-y-2">
            {(currentTask.thread || []).map((entry, idx) => (
              <div key={idx} className={`p-3 rounded-lg text-sm ${
                entry.type === 'reply'
                  ? 'bg-slate-800/50 border border-slate-700/50'
                  : 'bg-slate-800/30 border border-slate-700/30'
              }`}>
                <div className="flex items-center justify-between mb-1">
                  <span className="text-white font-medium text-xs">
                    {entry.author_name || entry.author?.split('@')[0] || 'Systeme'}
                  </span>
                  <span className="text-slate-500 text-xs">{timeAgo(entry.date)}</span>
                </div>
                <p className={`${entry.type === 'reply' ? 'text-slate-300' : 'text-slate-500 italic'}`}>
                  {entry.text}
                </p>
              </div>
            ))}
            {(!currentTask.thread || currentTask.thread.length === 0) && (
              <p className="text-sm text-slate-500 text-center py-4">Aucun message</p>
            )}
          </div>
        </div>
      </div>

      {/* Reply box */}
      <div className="p-4 border-t border-slate-700 bg-slate-900/50">
        <div className="flex gap-2">
          <input
            data-testid="task-reply-input"
            value={replyText}
            onChange={e => setReplyText(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && !e.shiftKey && sendReply()}
            className="flex-1 p-2.5 bg-slate-800 border border-slate-700 rounded-lg text-white text-sm placeholder-slate-500 focus:ring-2 focus:ring-blue-500 focus:outline-none"
            placeholder="Ecrire un message..."
          />
          <button
            data-testid="task-reply-send"
            onClick={sendReply}
            disabled={sending || !replyText.trim()}
            className="px-4 py-2.5 bg-blue-600 hover:bg-blue-500 disabled:opacity-50 rounded-lg text-white"
          >
            <Send className="w-4 h-4" />
          </button>
        </div>
      </div>
    </div>
  );
};

// ─── Main TasksPanel ──────────────────────────────────────────
export default function TasksPanel({ authUser, clientId, clientName, compact = false }) {
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [users, setUsers] = useState([]);
  const [showCreate, setShowCreate] = useState(false);
  const [selectedTask, setSelectedTask] = useState(null);
  const [statusFilter, setStatusFilter] = useState('all');

  const userEmail = authUser?.email || '';
  const userName = authUser?.name || '';

  const loadTasks = useCallback(async () => {
    setLoading(true);
    try {
      let url = `${API}/tasks?limit=100`;
      if (clientId) {
        url += `&client_id=${encodeURIComponent(clientId)}`;
        // Passer le nom de famille seul pour le matching (avant le premier espace)
        const nomFamille = (clientName || '').split(' ')[0];
        if (nomFamille) url += `&client_name=${encodeURIComponent(nomFamille)}`;
      }
      else url += `&assigned_to=${encodeURIComponent(userEmail)}&include_created=true`;

      const r = await authFetch(url);
      if (r.ok) {
        const d = await r.json();
        setTasks(d.tasks || []);
      }
    } catch (e) { console.error(e); }
    setLoading(false);
  }, [clientId, clientName, userEmail]);

  const loadUsers = useCallback(async () => {
    try {
      const r = await authFetch(`${API}/users/active`);
      if (r.ok) {
        const d = await r.json();
        setUsers(d.users || []);
      }
    } catch (e) { console.error(e); }
  }, []);

  useEffect(() => { loadTasks(); loadUsers(); }, [loadTasks, loadUsers]);

  const handleTaskCreated = () => { loadTasks(); };

  const handleTaskUpdated = (updated) => {
    setTasks(prev => prev.map(t => t.task_id === updated.task_id ? updated : t));
    if (selectedTask?.task_id === updated.task_id) setSelectedTask(updated);
  };

  const deleteTask = async (taskId) => {
    if (!window.confirm('Supprimer cette tache ?')) return;
    try {
      const r = await authFetch(`${API}/tasks/${taskId}?user_email=${encodeURIComponent(userEmail)}`, { method: 'DELETE' });
      if (r.ok) {
        setTasks(prev => prev.filter(t => t.task_id !== taskId));
        if (selectedTask?.task_id === taskId) setSelectedTask(null);
      }
    } catch (e) { console.error(e); }
  };

  const markRead = async (taskId) => {
    await authFetch(`${API}/tasks/${taskId}/read?user_email=${encodeURIComponent(userEmail)}`, { method: 'POST' }).catch(() => {});
  };

  const openTask = (task) => {
    markRead(task.task_id);
    setSelectedTask(task);
  };

  const filteredTasks = statusFilter === 'all'
    ? tasks
    : tasks.filter(t => t.status === statusFilter);

  const counts = {
    all: tasks.length,
    a_faire: tasks.filter(t => t.status === 'a_faire').length,
    en_cours: tasks.filter(t => t.status === 'en_cours').length,
    terminee: tasks.filter(t => t.status === 'terminee').length,
  };

  // ─── Detail View ────
  if (selectedTask) {
    return (
      <div className={compact ? 'h-[500px] flex flex-col' : 'h-full flex flex-col'}>
        <TaskDetailView
          task={selectedTask}
          onClose={() => setSelectedTask(null)}
          onUpdate={handleTaskUpdated}
          users={users}
          authorEmail={userEmail}
          authorName={userName}
        />
      </div>
    );
  }

  // ─── List View ──────
  return (
    <div className="space-y-4" data-testid="tasks-panel">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-semibold text-white flex items-center gap-2">
          <MessageSquare className="w-5 h-5 text-blue-400" />
          {clientId ? 'Taches internes' : 'Mes taches'}
        </h2>
        <button
          data-testid="tasks-create-btn"
          onClick={() => setShowCreate(true)}
          className="px-4 py-2 bg-blue-600 hover:bg-blue-500 rounded-lg flex items-center gap-2 text-white text-sm"
        >
          <Plus className="w-4 h-4" /> Nouvelle tache
        </button>
      </div>

      {/* Filters */}
      <div className="flex gap-2 flex-wrap">
        {[
          { key: 'all', label: 'Toutes' },
          { key: 'a_faire', label: 'A faire' },
          { key: 'en_cours', label: 'En cours' },
          { key: 'terminee', label: 'Terminees' }
        ].map(f => (
          <button
            key={f.key}
            data-testid={`tasks-filter-${f.key}`}
            onClick={() => setStatusFilter(f.key)}
            className={`px-3 py-1.5 rounded-lg text-sm transition-colors ${
              statusFilter === f.key
                ? 'bg-blue-500/20 text-blue-400 border border-blue-500/30'
                : 'bg-slate-800 text-slate-400 hover:bg-slate-700'
            }`}
          >
            {f.label} ({counts[f.key]})
          </button>
        ))}
      </div>

      {/* Tasks List */}
      {loading ? (
        <div className="flex items-center justify-center py-12">
          <div className="animate-spin w-8 h-8 border-2 border-blue-500 border-t-transparent rounded-full" />
        </div>
      ) : filteredTasks.length === 0 ? (
        <div className="text-center py-12 bg-slate-900/50 rounded-xl border border-slate-800">
          <MessageSquare className="w-12 h-12 text-slate-600 mx-auto mb-3" />
          <p className="text-slate-400">Aucune tache</p>
          <button
            onClick={() => setShowCreate(true)}
            className="mt-3 px-4 py-2 bg-blue-600 hover:bg-blue-500 rounded-lg inline-flex items-center gap-2 text-white text-sm"
          >
            <Plus className="w-4 h-4" /> Creer une tache
          </button>
        </div>
      ) : (
        <div className="space-y-2">
          {filteredTasks.map(task => {
            const prio = PRIORITY_CONFIG[task.priority] || PRIORITY_CONFIG.normal;
            const stat = STATUS_CONFIG[task.status] || STATUS_CONFIG.a_faire;
            const isUnread = !task.read_by?.includes(userEmail);
            const threadCount = (task.thread || []).filter(t => t.type === 'reply').length;

            return (
              <div
                key={task.task_id}
                data-testid={`task-row-${task.task_id}`}
                onClick={() => openTask(task)}
                className={`p-4 rounded-xl border cursor-pointer transition-all hover:border-blue-500/50 ${
                  isUnread
                    ? 'bg-blue-500/5 border-blue-500/20'
                    : task.status === 'terminee'
                      ? 'bg-slate-900/30 border-slate-800 opacity-70'
                      : 'bg-slate-900/50 border-slate-800'
                }`}
              >
                <div className="flex items-start justify-between gap-3">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      {isUnread && <span className="w-2 h-2 rounded-full bg-blue-400 shrink-0" />}
                      <span className={`w-2 h-2 rounded-full ${prio.dot} shrink-0`} />
                      <h4 className={`text-sm font-medium truncate ${task.status === 'terminee' ? 'line-through text-slate-500' : 'text-white'}`}>
                        {task.title}
                      </h4>
                    </div>
                    <div className="flex items-center gap-2 mt-1.5 flex-wrap">
                      <span className={`text-xs px-2 py-0.5 rounded ${stat.bg} ${stat.text}`}>{stat.label}</span>
                      <span className={`text-xs px-2 py-0.5 rounded ${prio.bg} ${prio.text}`}>{prio.label}</span>
                      {task.client_name && (
                        <span className="text-xs text-slate-500 flex items-center gap-1">
                          <User className="w-3 h-3" />{task.client_name}
                        </span>
                      )}
                      {threadCount > 0 && (
                        <span className="text-xs text-slate-500 flex items-center gap-1">
                          <MessageSquare className="w-3 h-3" />{threadCount}
                        </span>
                      )}
                      {(task.assigned_to || []).length > 0 && (
                        <span className="text-xs text-slate-500 flex items-center gap-1">
                          <Users className="w-3 h-3" />{task.assigned_to.length}
                        </span>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    <span className="text-xs text-slate-600">
                      {task.updated_at ? new Date(task.updated_at).toLocaleDateString('fr-FR', { day: 'numeric', month: 'short' }) : ''}
                    </span>
                    <button
                      onClick={e => { e.stopPropagation(); deleteTask(task.task_id); }}
                      className="p-1.5 text-slate-600 hover:text-red-400 hover:bg-red-500/10 rounded transition-colors"
                      data-testid={`task-delete-${task.task_id}`}
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Create Modal */}
      {showCreate && (
        <TaskCreateModal
          onClose={() => setShowCreate(false)}
          onCreate={handleTaskCreated}
          users={users}
          clientId={clientId}
          clientName={clientName}
          authorEmail={userEmail}
          authorName={userName}
        />
      )}
    </div>
  );
}
